import { createSlice } from "@reduxjs/toolkit";
import { matchesLibrarySave } from "../library/libraryState";

const initialState = {
   // Common states
   activeStep: 0,
   uploadedFiles: [],
   uploadedFileRes: [],
   loading: false,
   analysisReport: null,
   analysisLibrary: null,

   // Quantitative analysis specific states
   quantitativeStep: 1, // default step for quantitative
   quantitativeAnalysisDone: false,
   quantitativeDownloadUrl: null,
   quantitativeAnalysisLoading: false,
   quantitativeUploadedUrls: [],
   quantitativeFileUrl: null,
   quantitativeFinalReportUrl: null,
   quantitativeLibrary: null,
   quantitativePrompt: "",
   quantitativeUploading: false,
   quantitativeExcelSheets: {},
   quantitativeSelectedSheets: {},
};

export const analysisSlice = createSlice({
   name: "analysis",
   initialState,
   reducers: {
      // Common actions
      setActiveStep: (state, action) => {
         state.activeStep = action.payload;
      },
      nextStep: (state) => {
         state.activeStep += 1;
      },
      previousStep: (state) => {
         state.activeStep -= 1;
      },
      resetStep: (state) => {
         state.activeStep = 0;
      },

      addUploadedFiles: (state, action) => {
         state.uploadedFiles = [
            ...state.uploadedFiles,
            ...action.payload.map((file) => ({
               name: file.name,
               size: file.size,
               type: file.type,
            })),
         ];
      },
      addUploadedFileRes: (state, action) => {
         state.uploadedFileRes = [...state.uploadedFileRes, ...action.payload];
      },
      removeUploadedFile: (state, action) => {
         const index = action.payload;
         const fileToRemove = state.uploadedFiles[index];
         state.uploadedFiles = state.uploadedFiles.filter(
            (_, i) => i !== index
         );
         state.uploadedFileRes = state.uploadedFileRes.filter(
            (res) => !res.includes(fileToRemove.name)
         );
      },
      clearUploadedData: (state) => {
         state.uploadedFiles = [];
         state.uploadedFileRes = [];
      },

      setLoading: (state, action) => {
         state.loading = action.payload;
      },
      setAnalysisReport: (state, action) => {
         state.analysisReport = action.payload?.url ?? action.payload;
         state.analysisLibrary = action.payload?.library || null;
      },
      clearAnalysisReport: (state) => {
         state.analysisLibrary = null;
         state.analysisReport = initialState.analysisReport;
      },

      // Quantitative analysis specific actions
      setQuantitativeExcelSheets: (state, action) => {
         state.quantitativeExcelSheets = { ...state.quantitativeExcelSheets, ...action.payload };
      },
      setQuantitativeSelectedSheets: (state, action) => {
         state.quantitativeSelectedSheets = action.payload;
      },
      setQuantitativeStep: (state, action) => {
         state.quantitativeStep = action.payload;
      },
      setQuantitativeAnalysisDone: (state, action) => {
         state.quantitativeAnalysisDone = action.payload;
      },
      setQuantitativeDownloadUrl: (state, action) => {
         state.quantitativeDownloadUrl = action.payload;
      },
      setQuantitativeAnalysisLoading: (state, action) => {
         state.quantitativeAnalysisLoading = action.payload;
      },
      setQuantitativeUploadedUrls: (state, action) => {
         state.quantitativeUploadedUrls = [...state.quantitativeUploadedUrls, ...action.payload];
      },
      setQuantitativeFileUrl: (state, action) => {
         state.quantitativeFileUrl = action.payload;
      },
      setQuantitativeFinalReportUrl: (state, action) => {
         state.quantitativeFinalReportUrl = action.payload?.url ?? action.payload;
         state.quantitativeLibrary = action.payload?.library || null;
      },
      setQuantitativePrompt: (state, action) => {
         state.quantitativePrompt = action.payload;
      },
      setQuantitativeUploading: (state, action) => {
         state.quantitativeUploading = action.payload;
      },
      resetQuantitativeState: (state) => {
         state.quantitativeLibrary = null;
         // Reset only quantitative specific state, keep uploaded files
         state.quantitativeStep = initialState.quantitativeStep;
         state.quantitativeAnalysisDone = initialState.quantitativeAnalysisDone;
         state.quantitativeDownloadUrl = initialState.quantitativeDownloadUrl;
         state.quantitativeAnalysisLoading = initialState.quantitativeAnalysisLoading;
         state.quantitativeUploadedUrls = initialState.quantitativeUploadedUrls;
         state.quantitativeFileUrl = initialState.quantitativeFileUrl;
         state.quantitativeFinalReportUrl = initialState.quantitativeFinalReportUrl;
         state.quantitativePrompt = initialState.quantitativePrompt;
         state.quantitativeUploading = initialState.quantitativeUploading;
         state.quantitativeExcelSheets = initialState.quantitativeExcelSheets;
         state.quantitativeSelectedSheets = initialState.quantitativeSelectedSheets;
      },
      resetAnalysis: () => initialState,
   },
   extraReducers: (builder) => {
      builder.addCase("library/saveConfirmed", (state, action) => {
         if (matchesLibrarySave(state.analysisLibrary, action)) state.analysisLibrary = action.payload;
         if (matchesLibrarySave(state.quantitativeLibrary, action)) state.quantitativeLibrary = action.payload;
      });
   },
});

export const {
   setActiveStep,
   nextStep,
   previousStep,
   resetStep,
   addUploadedFiles,
   addUploadedFileRes,
   removeUploadedFile,
   clearUploadedData,
   setLoading,
   setAnalysisReport,
   resetAnalysis,
   clearAnalysisReport,
   // Quantitative actions
   setQuantitativeStep,
   setQuantitativeAnalysisDone,
   setQuantitativeDownloadUrl,
   setQuantitativeAnalysisLoading,
   setQuantitativeUploadedUrls,
   setQuantitativeFileUrl,
   setQuantitativeFinalReportUrl,
   setQuantitativePrompt,
   setQuantitativeUploading,
   setQuantitativeExcelSheets,
   setQuantitativeSelectedSheets,
   resetQuantitativeState,
} = analysisSlice.actions;

export default analysisSlice.reducer;
